import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import { HandHelping, CheckCircle2, XCircle, Clock, Video, MapPin, Inbox, Send } from "lucide-react";

const TABS = [
    { key: "pending", label: "Primite (pending)", Icon: Inbox },
    { key: "sent",    label: "Trimise",          Icon: Send },
];

export default function AssistancePage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("pending");
    const [pending, setPending] = useState([]);
    const [sent, setSent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [responseNote, setResponseNote] = useState({});

    const fetchAll = () => {
        setLoading(true);
        Promise.all([
            API.get("/assistance/pending").then(r => setPending(r.data)),
            API.get("/assistance/sent").then(r => setSent(r.data)),
        ]).catch(() => toast.error("Eroare la încărcare"))
          .finally(() => setLoading(false));
    };

    useEffect(() => { fetchAll(); }, []);

    const respond = async (reqId, action) => {
        try {
            const r = await API.post(`/assistance/${reqId}/respond`, {
                action,
                response_note: responseNote[reqId] || ""
            });
            if (action === "accept") {
                if (r.data.event_id) {
                    toast.success("Cerere acceptată • meeting adăugat în calendar");
                } else {
                    toast.success("Cerere acceptată");
                }
            } else {
                toast.success("Cerere refuzată");
            }
            fetchAll();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const renderMeetingInfo = (r) => {
        if (!r.with_meeting) return null;
        const TypeIcon = r.meeting_type === "meeting_physical" ? MapPin : Video;
        return (
            <div className="flex items-center gap-2 text-xs text-[#4B5563] dark:text-[#B5BAC1] mt-2 bg-[#F8F9FA] dark:bg-[#0B0D10] rounded-md px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33]">
                <TypeIcon size={13} className="text-[#002FA7] dark:text-[#6A91FF]" />
                <span className="font-semibold">Meeting propus:</span>
                <span>{new Date(r.meeting_start).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                {r.meeting_end && r.meeting_end !== r.meeting_start && (
                    <span className="text-[#9CA3AF]">– {new Date(r.meeting_end).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}</span>
                )}
            </div>
        );
    };

    const StatusBadge = ({ status }) => {
        if (status === "pending") return <span className="text-[10px] font-bold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">în așteptare</span>;
        if (status === "accepted") return <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">acceptată</span>;
        if (status === "declined") return <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">refuzată</span>;
        return <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{status}</span>;
    };

    const items = tab === "pending" ? pending : sent;

    return (
        <div className="p-6 max-w-3xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-md bg-[#002FA7] text-white flex items-center justify-center">
                    <HandHelping size={20} />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Asistență</h1>
                    <p className="text-sm text-[#9CA3AF] dark:text-[#7E858E]">Cereri de ajutor între colegi</p>
                </div>
            </div>

            <div className="flex gap-1 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-0.5 mb-4 w-fit">
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        data-testid={`assistance-tab-${t.key}`}
                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded transition-colors ${tab === t.key ? "bg-[#002FA7] text-white" : "text-[#4B5563] hover:bg-[#F1F3F5]"}`}>
                        <t.Icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="space-y-2">
                    {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white border border-[#E4E4E7] rounded-md animate-pulse" />)}
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-[#151A21] rounded-md border border-[#E4E4E7] dark:border-[#252B33]">
                    <HandHelping size={40} className="text-[#E4E4E7] mx-auto mb-3" />
                    <p className="text-sm text-[#9CA3AF]">
                        {tab === "pending" ? "Nu ai cereri de asistență" : "Nu ai trimis cereri de asistență"}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map(r => (
                        <div key={r.id} className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-lg p-4" data-testid={`assistance-item-${r.id}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    {tab === "pending" ? (
                                        <>
                                            <UserAvatar user={r.from_user} className="w-7 h-7" textClassName="text-xs" />
                                            <span className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6]">{r.from_user?.name}</span>
                                            <span className="text-xs text-[#9CA3AF]">îți cere asistență</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-xs text-[#9CA3AF]">către</span>
                                            <UserAvatar user={r.to_user} className="w-7 h-7" textClassName="text-xs" />
                                            <span className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6]">{r.to_user?.name}</span>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <StatusBadge status={r.status} />
                                    <span className="text-[10px] text-[#9CA3AF]">
                                        {new Date(r.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                </div>
                            </div>
                            {r.task && (
                                <button onClick={() => r.task.board_id && navigate(`/boards/${r.task.board_id}?task=${r.task.id || r.task_id}`)}
                                    className="text-xs text-[#002FA7] dark:text-[#6A91FF] font-semibold hover:underline mb-2 block">
                                    Task: {r.task.title}
                                </button>
                            )}
                            <p className="text-sm text-[#4B5563] dark:text-[#B5BAC1] whitespace-pre-wrap">{r.message}</p>
                            {renderMeetingInfo(r)}
                            {r.response_note && (
                                <p className="text-xs text-[#9CA3AF] mt-2 italic">Răspuns: {r.response_note}</p>
                            )}

                            {tab === "pending" && r.status === "pending" && (
                                <div className="mt-3 space-y-2 border-t border-[#E4E4E7] dark:border-[#252B33] pt-3">
                                    <textarea
                                        value={responseNote[r.id] || ""}
                                        onChange={e => setResponseNote({ ...responseNote, [r.id]: e.target.value })}
                                        placeholder="Notificare (opțional)..."
                                        rows={2}
                                        className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-xs" />
                                    <div className="flex gap-2">
                                        <button onClick={() => respond(r.id, "accept")}
                                            data-testid={`assistance-accept-${r.id}`}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md text-sm font-semibold">
                                            <CheckCircle2 size={14} /> Accept{r.with_meeting ? " (adaugă meetingul)" : ""}
                                        </button>
                                        <button onClick={() => respond(r.id, "decline")}
                                            data-testid={`assistance-decline-${r.id}`}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-red-100 text-red-700 hover:bg-red-200 py-2 rounded-md text-sm font-semibold">
                                            <XCircle size={14} /> Refuz
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
